import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { LayoutShell } from '../../shared/layouts';
import { adminService, IAdminBusinessView } from '../../shared/services/api/adminService';
import { feedback } from '../../shared/services';
import { Button } from '../../shared/components/MUI/button/Button';

export const AdminBusinesses: React.FC = () => {
  const [selectedBusiness, setSelectedBusiness] = useState<IAdminBusinessView | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data: businesses, isFetching } = useQuery({
    queryKey: ['admin', 'businesses'],
    queryFn: () => adminService.getAllBusinesses(),
  });

  const { mutate: toggleBlock, isLoading: isToggling } = useMutation(
    (id: number) => adminService.toggleBlock(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin', 'businesses']);
        feedback(
          selectedBusiness?.isBlocked
            ? 'Estabelecimento desbloqueado com sucesso!'
            : 'Estabelecimento bloqueado com sucesso!',
          'success',
        );
        setModalOpen(false);
        setSelectedBusiness(null);
      },
      onError: (error: Error) => {
        feedback(error.message, 'error');
      },
    },
  );

  const handleOpenModal = (business: IAdminBusinessView) => {
    setSelectedBusiness(business);
    setModalOpen(true);
  };

  const handleConfirm = () => {
    if (selectedBusiness) {
      toggleBlock(selectedBusiness.id);
    }
  };

  return (
    <LayoutShell>
      <Box display='flex' flexDirection='column' gap={3}>
        <Typography variant='h5' fontWeight={600}>
          Estabelecimentos
        </Typography>

        <TableContainer component={Paper} variant='outlined'>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Nome</strong></TableCell>
                <TableCell><strong>Proprietário</strong></TableCell>
                <TableCell><strong>E-mail</strong></TableCell>
                <TableCell><strong>Telefone</strong></TableCell>
                <TableCell align='center'><strong>Func.</strong></TableCell>
                <TableCell align='center'><strong>Agend. (mês)</strong></TableCell>
                <TableCell align='center'><strong>Mensagens</strong></TableCell>
                <TableCell align='center'><strong>Status</strong></TableCell>
                <TableCell align='center'><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching && (
                <TableRow>
                  <TableCell colSpan={10} align='center'>
                    <Typography color='text.secondary'>Carregando...</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!isFetching && businesses?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align='center'>
                    <Typography color='text.secondary'>Nenhum estabelecimento encontrado.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {businesses?.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell>{b.id}</TableCell>
                  <TableCell>{b.name}</TableCell>
                  <TableCell>{b.ownerName || '-'}</TableCell>
                  <TableCell>{b.ownerEmail || '-'}</TableCell>
                  <TableCell>
                    <Box display='flex' alignItems='center' gap={1}>
                      {b.ownerPhone || '-'}
                      {b.ownerPhone && (
                        <IconButton
                          size='small'
                          color='success'
                          component='a'
                          href={`https://wa.me/${b.ownerPhone.replace(/\D/g, '')}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          title='Chamar no WhatsApp'
                        >
                          <Icon>whatsapp</Icon>
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align='center'>{b.employeeCount}</TableCell>
                  <TableCell align='center'>{b.appointmentsThisMonth}</TableCell>
                  <TableCell align='center'>{b.totalMessagesSent}</TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={b.isBlocked ? 'Bloqueado' : 'Ativo'}
                      color={b.isBlocked ? 'error' : 'success'}
                      size='small'
                      variant='outlined'
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton
                      color={b.isBlocked ? 'success' : 'error'}
                      size='small'
                      onClick={() => handleOpenModal(b)}
                      title={b.isBlocked ? 'Desbloquear' : 'Bloquear'}
                    >
                      <Icon>{b.isBlocked ? 'lock_open' : 'lock'}</Icon>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Confirmation Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle>
          {selectedBusiness?.isBlocked ? 'Desbloquear estabelecimento' : 'Bloquear estabelecimento'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {selectedBusiness?.isBlocked
              ? `Tem certeza que deseja desbloquear "${selectedBusiness?.name}"? O proprietário poderá acessar o sistema novamente.`
              : `Tem certeza que deseja bloquear "${selectedBusiness?.name}"? O proprietário perderá o acesso ao sistema até ser desbloqueado.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            label='Cancelar'
            variant='outlined'
            onClick={() => { setModalOpen(false); setSelectedBusiness(null); }}
          />
          <Button
            label={selectedBusiness?.isBlocked ? 'Desbloquear' : 'Bloquear'}
            variant='contained'
            color={selectedBusiness?.isBlocked ? 'success' : 'error'}
            onClick={handleConfirm}
            isLoading={isToggling}
          />
        </DialogActions>
      </Dialog>
    </LayoutShell>
  );
};
